



































































































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import { FeedTank, Node, Sensor, SensorsApi, Transceiver } from '@/api';
import { FeedProductModals } from '@/pages/account/products/feed/models';

import TransceiverSuggest from '@/components/form pickers/transceiverSuggest.vue';
import SensorSuggest from '@/components/form pickers/sensorSuggest.vue';
import Toast from '@/components/display/toast.vue';
import SaveCancelButtons from '@/components/elements/buttons/save-cancel-buttons.vue';
import NodeSuggest from '@/components/form pickers/nodeSuggest.vue';
import { VueSelect } from 'vue-select';
import { isStrain, isRadar, SensorType } from '@/pages/account/products/feed/models/sensor-config';

type ConnectionType = 'transceiver' | 'lorawan_gateway';
type GatewayMode = 'deploying' | 'already_available';

type InstallFeedTank = FeedTank & {
	/** Radar: single sensor. */
	sensor?: Sensor;
	/** Strain: 0 to many sensors. */
	sensors?: Sensor[];
	transceiver?: Transceiver;
};

@Component({
	components: {
		NodeSuggest,
		SaveCancelButtons,
		TransceiverSuggest,
		SensorSuggest,
		VueSelect,
	},
	methods: {
		isRadar,
	},
})
export default class InstallFeedTankModal extends Vue {
	@Prop({ required: true, type: Object })
	public feedTank!: InstallFeedTank;

	@Prop({ required: true, type: Function })
	private onInstall!: (feedTank: FeedTank) => Promise<void>;

	@Watch('feedTank')
	public onFeedTankChanged(): void {
		this.resetValues();
	}

	public get transceiverId(): number | null {
		return this.transceiver?.id ?? null;
	}

	public readonly connectionOptions: Array<{ value: ConnectionType; label: string }> = [
		{ value: 'lorawan_gateway', label: 'LoRaWAN Gateway' },
		{ value: 'transceiver', label: 'Transceiver' },
	];

	public connectionType: ConnectionType = 'lorawan_gateway';

	public readonly gatewayModeOptions: Array<{ value: GatewayMode; label: string }> = [
		{ value: 'deploying', label: 'Deploying Gateway' },
		{ value: 'already_available', label: 'Already Available' },
	];

	public gatewayMode: GatewayMode = 'deploying';

	public sensorType: SensorType = (this.feedTank.sensorType as SensorType) ?? 'radar';
	public transceiver: Partial<Transceiver> | null = this.feedTank.transceiver ?? null;
	public sensor: Sensor | null = this.feedTank.sensor ?? null;
	public node: Node | null = this.feedTank.sensor?.node ?? null;
	/** Strain: selected sensors. */
	public sensorList: Sensor[] = this.feedTank.sensors ?? [];
	/** Strain: sensors offered in the dropdown. */
	public suggestedSensors: Sensor[] = [];
	public isSuggestedSensorsLoading = false;

	public get availableSensors(): Sensor[] {
		const selectedIds = new Set(this.sensorList.map(s => s.id));
		return this.suggestedSensors.filter(s => !selectedIds.has(s.id));
	}

	public onSensorSelected(selected: Sensor[]): void {
		this.sensorList = selected ?? [];
	}

	public modalId: string = FeedProductModals.INSTALL_FEED_TANK;

	/** Serial number of the transceiver selected before the most recent change. */
	private previousTransceiverSerial: string | null =
		this.feedTank.transceiver?.serialNumber ?? null;

	private toast: Toast = new Toast();
	private readonly sensorsApi = new SensorsApi();

	public async fetchSuggestedSensors({ preserveTankSensors = false } = {}): Promise<void> {
		if (this.connectionType === 'transceiver' && this.transceiverId == null) {
			this.suggestedSensors = [];
			return;
		}
		this.isSuggestedSensorsLoading = true;
		try {
			const params: Record<string, string | number | null> = {
				include: 'node,transceiver.site',
				'filter[type]': 'strainGauge',
				'filter[status]': 'unallocated',
				perPage: 500,
			};
			// LoRaWAN sensors connect wirelessly — no transceiver filter needed.
			if (this.connectionType === 'transceiver') {
				params['filter[transceiverId]'] = this.transceiverId;
			}
			const response = await this.sensorsApi.getSensors({ params });
			const installedSensors = (this.feedTank.sensors ?? []).filter(
				s => this.connectionType !== 'transceiver' || s.transceiverId === this.transceiverId,
			);
			const installedSensorIds = new Set(installedSensors.map(s => s.id));
			this.suggestedSensors = [
				...(response.data.data ?? []).filter(s => !installedSensorIds.has(s.id)),
				...installedSensors,
			];
			// On initial load, hydrate sensorList from the tank's previously installed sensors.
			const isSameTransceiverAsTank = this.transceiverId === this.feedTank.transceiver?.id;
			if (
				preserveTankSensors &&
				isSameTransceiverAsTank &&
				(this.feedTank.sensors?.length ?? 0) > 0
			) {
				const selectedIds = new Set(this.feedTank.sensors.map(s => s?.id));
				this.sensorList = this.suggestedSensors.filter(s => selectedIds.has(s.id));
			}
		} catch (e) {
			console.error(e);
			this.suggestedSensors = [];
		} finally {
			this.isSuggestedSensorsLoading = false;
		}
	}

	private isChimeraTransceiver(serialNumber?: string | null): boolean {
		return !!serialNumber && serialNumber.toUpperCase().startsWith('CH');
	}

	public onConnectionTypeChanged(type: ConnectionType): void {
		if (this.connectionType === type) return;
		// Chimera sensors are physically wired to the transceiver — clear when leaving a Chimera.
		// Non-Chimera sensors are independent — preserve across connection type switches.
		if (this.isChimeraTransceiver(this.transceiver?.serialNumber)) {
			this.sensorList = [];
		}
		this.connectionType = type;
		this.gatewayMode = 'deploying';
		this.transceiver = null;
		this.previousTransceiverSerial = null;
		this.sensor = null;
		this.node = null;
		this.suggestedSensors = [];
		if (type === 'lorawan_gateway') {
			this.fetchSuggestedSensors({ preserveTankSensors: true });
		}
	}

	public onGatewayModeChanged(mode: GatewayMode): void {
		if (this.gatewayMode === mode) return;
		this.gatewayMode = mode;
		this.transceiver = null;
		this.previousTransceiverSerial = null;
		this.fetchSuggestedSensors({ preserveTankSensors: true });
	}

	public onTransceiverChanged(transceiver: Partial<Transceiver> | null): void {
		const prevSerial = this.previousTransceiverSerial;
		this.previousTransceiverSerial = transceiver?.serialNumber ?? null;
		if (prevSerial === this.previousTransceiverSerial) return;

		if (transceiver === null) {
			this.sensor = null;
			this.node = null;
			if (this.connectionType === 'transceiver') {
				this.sensorList = [];
				this.suggestedSensors = [];
			}
			return;
		}

		const previousWasChimera = this.isChimeraTransceiver(prevSerial);
		const newIsChimera = this.isChimeraTransceiver(transceiver.serialNumber);

		if (isRadar(this.sensorType)) {
			// Chimera → Chimera: preserve sensor. Any other swap: clear.
			if (!(previousWasChimera && newIsChimera)) {
				this.sensor = null;
				this.node = null;
			}
		} else if (isStrain(this.sensorType) && this.connectionType === 'transceiver') {
			const shouldClear = previousWasChimera !== newIsChimera;
			if (shouldClear) {
				this.sensorList = [];
			}
			this.fetchSuggestedSensors({ preserveTankSensors: true });
		}
	}

	public async installFeedTank(): Promise<void> {
		const requiresGateway =
			this.connectionType === 'lorawan_gateway' && this.gatewayMode === 'deploying';
		if (!this.transceiver && (this.connectionType === 'transceiver' || requiresGateway)) {
			const label = this.connectionType === 'lorawan_gateway' ? 'LoRaWAN Gateway' : 'transceiver';
			this.toast.makeToast(`You must select a ${label}`, 'Error', 'danger');
			return;
		}
		if (isRadar(this.sensorType) && this.sensor === null) {
			this.toast.makeToast('You must select a sensor', 'Error', 'danger');
			return;
		} else if (isStrain(this.sensorType) && this.sensorList.length === 0) {
			this.toast.makeToast('You must select at least one sensor', 'Error', 'danger');
			return;
		}

		const payload: FeedTank = {
			...this.feedTank,
			sensor: this.sensor ?? undefined,
			sensors: isStrain(this.sensorType)
				? this.sensorList
				: this.sensor != null
				? [this.sensor]
				: [],
			transceiver: this.transceiver as Transceiver,
		};
		await this.onInstall(payload);
	}

	public dismiss(): void {
		this.$bvModal.hide(FeedProductModals.INSTALL_FEED_TANK);
	}

	private resetValues(): void {
		const existingTransceiver = this.feedTank.transceiver ?? null;
		const isExistingLoraWAN = existingTransceiver?.type === 'lorawan_gateway';
		this.connectionType =
			existingTransceiver && !isExistingLoraWAN ? 'transceiver' : 'lorawan_gateway';
		this.gatewayMode = isExistingLoraWAN ? 'already_available' : 'deploying';
		this.transceiver = existingTransceiver;
		this.previousTransceiverSerial = existingTransceiver?.serialNumber ?? null;
		this.sensorType = (this.feedTank.sensorType as SensorType) ?? 'radar';
		this.sensor = this.feedTank.sensor ?? null;
		this.node = this.feedTank.sensor?.node ?? null;
		this.sensorList = this.feedTank.sensors ?? [];
		this.suggestedSensors = [];
		if (isStrain(this.sensorType)) {
			this.fetchSuggestedSensors();
		}
	}
}
